/*
 * Реестр маршрутов: адрес ↔ место в приложении.
 *
 * Место — раздел и стопка слоёв над его корнем. Первый сегмент пути называет
 * раздел, остаток разбирает маршрут раздела: parse(сегменты) → слои,
 * path(слои) → сегменты. Слои несут только идентификаторы и вкладки, данные
 * экран догружает сам. Сегмент, который раздел не понимает, обрезает путь;
 * неизвестный корень открывает Главную. Здесь нет React и window — всё
 * проверяется тестами (routes.test.ts).
 */

import type { DealPreset } from '../contracts/purpose';
import { FUNCTIONS, type FunctionName, type WorkTab } from '../functions/functions';

export type Section =
  | 'home' | 'kb' | 'projects' | 'chat' | 'calendar' | 'contracts' | 'legal' | 'finance' | 'tasks' | 'admin' | 'profile' | 'more';

/** Слой стопки: ключ уникален в стопке, по kind раздел решает, что рисовать. */
export type Layer = { key: string; kind: string };

/** Маршрут раздела. Методы, а не свойства-функции: так Route<KbLayer> присваивается Route. */
export type Route<T extends Layer = Layer> = {
  parse(segments: string[]): T[];
  path(layers: T[]): string[];
};

export type Place = { section: Section; layers: Layer[] };

/** Первый сегмент пути у раздела. Главная — пустой, то есть `/`. */
const ROOT: Record<Section, string> = {
  home: '', kb: 'kb', projects: 'projects', chat: 'chat', calendar: 'calendar',
  contracts: 'contracts', legal: 'legal', finance: 'finance', tasks: 'tasks', admin: 'admin', profile: 'profile', more: 'more',
};

/** Идентификаторы и коды в пути: только это, иначе сегмент не разбирается. */
const ID = /^[A-Za-z0-9_-]+$/;

// --- база знаний -------------------------------------------------------------

export type KbPage = 'org' | 'groups' | 'gloss' | 'map';
const KB_PAGES: KbPage[] = ['org', 'groups', 'gloss', 'map'];

export type KbLayer =
  | { key: string; kind: 'page'; page: KbPage }
  | { key: string; kind: 'proc'; code: string; target?: number };

export const kbPageLayer = (page: KbPage): KbLayer => ({ key: `page:${page}`, kind: 'page', page });
export const procLayer = (code: string, target?: number): KbLayer =>
  target === undefined ? { key: `proc:${code}`, kind: 'proc', code } : { key: `proc:${code}`, kind: 'proc', code, target };

const kb: Route<KbLayer> = {
  parse(seg) {
    const out: KbLayer[] = [];
    let i = 0;
    const page = KB_PAGES.find((p) => p === seg[0]);
    if (page) {
      out.push(kbPageLayer(page));
      i = 1;
    }
    if (seg[i] === 'proc' && seg[i + 1] && ID.test(seg[i + 1])) out.push(procLayer(seg[i + 1]));
    return out;
  },
  path(layers) {
    const out: string[] = [];
    // в адресе верхняя страница: карта поверх групп — /kb/map
    const page = [...layers].reverse().find((l) => l.kind === 'page');
    if (page?.kind === 'page') out.push(page.page);
    // В адресе только верхний процесс: цепочка переходов между процессами
    // живёт в истории браузера, адрес её не повторяет.
    const proc = [...layers].reverse().find((l) => l.kind === 'proc');
    if (proc?.kind === 'proc') out.push('proc', proc.code);
    return out;
  },
};

// --- проекты -----------------------------------------------------------------

/** Вкладка экрана работы (FUNCTION_TABS): прежние подразделы проекта и «Договоры». */
export type ProjSection = WorkTab;
export const PROJ_SECTIONS: ProjSection[] = ['overview', 'docs', 'gpr', 'packages', 'sprints', 'walks', 'model', 'team', 'contracts', 'finance', 'settings'];

/** Вкладки карточки собственника: здания и досье его юрлица. */
export type OwnerTab = 'buildings' | 'docs';

export type ProjectsLayer =
  | { key: string; kind: 'project'; id: string; section: ProjSection }
  /** Вкладка «Функций» — состояние корня, как подраздел «Договоров»: ключ один, смена вкладки заменяет слой. */
  | { key: 'fn'; kind: 'fn'; fn: FunctionName }
  /** Карточка собственника поверх вкладки «Собственник» (спека 2026-10-01). */
  | { key: string; kind: 'owner'; id: string; tab: OwnerTab }
  /** Форма «Завести здание» внутри собственника — слой без адреса. */
  | { key: string; kind: 'newBuilding'; ownerId: string };

export const projectLayer = (id: string, section: ProjSection = 'overview'): ProjectsLayer =>
  ({ key: `project:${id}`, kind: 'project', id, section });

/** Вкладка функции в адресе списка: /projects/owners … Сегмент не совпадает ни с одним cuid работы. */
const FN_SEGMENT: Record<FunctionName, string> = {
  OWNER: 'owners', BROKER: 'broker', DESIGN: 'design', BUILD: 'build', MANAGE: 'manage',
};
export const fnLayer = (fn: FunctionName): ProjectsLayer => ({ key: 'fn', kind: 'fn', fn });
/** Карточка собственника: смена вкладки — тот же ключ, вершина стопки заменяется. */
export const ownerLayer = (id: string, tab: OwnerTab = 'buildings'): ProjectsLayer => ({ key: `owner:${id}`, kind: 'owner', id, tab });
/** «Завести здание» — слой без адреса, как форма нового договора; собственник в ключе. */
export const newBuildingLayer = (ownerId: string): ProjectsLayer => ({ key: `new-building:${ownerId}`, kind: 'newBuilding', ownerId });

const projects: Route<ProjectsLayer> = {
  parse(seg) {
    // Один сегмент-функция — вкладка «Функций»; иначе прежний адрес работы.
    const fn = FUNCTIONS.find((f) => FN_SEGMENT[f] === seg[0]);
    if (fn && seg.length === 1) return [fnLayer(fn)];
    // /projects/owners/<id>[/docs] — карточка собственника поверх своей вкладки.
    if (seg[0] === FN_SEGMENT.OWNER && seg[1] && ID.test(seg[1])) {
      return [fnLayer('OWNER'), ownerLayer(seg[1], seg[2] === 'docs' ? 'docs' : 'buildings')];
    }
    if (!seg[0] || !ID.test(seg[0])) return [];
    // подраздела нет или он выдуман — открываем обзор
    const section = PROJ_SECTIONS.find((s) => s === seg[1]) ?? 'overview';
    return [projectLayer(seg[0], section)];
  },
  path(layers) {
    const p = layers.find((l) => l.kind === 'project');
    if (p?.kind === 'project') return [p.id, p.section];
    const o = layers.find((l) => l.kind === 'owner');
    if (o?.kind === 'owner') return o.tab === 'docs' ? [FN_SEGMENT.OWNER, o.id, 'docs'] : [FN_SEGMENT.OWNER, o.id];
    const f = layers.find((l) => l.kind === 'fn');
    return f?.kind === 'fn' ? [FN_SEGMENT[f.fn]] : [];
  },
};

// --- чат ---------------------------------------------------------------------

/** Слой переписки; остальные слои чата (ветка, человек, диалог) адреса на этом этапе не имеют. */
export type ChatRouteLayer = { key: string; kind: 'chat'; id: string };
export const chatRouteLayer = (id: string): ChatRouteLayer => ({ key: `chat:${id}`, kind: 'chat', id });

const chat: Route<Layer> = {
  parse(seg) {
    return seg[0] && ID.test(seg[0]) ? [chatRouteLayer(seg[0])] : [];
  },
  path(layers) {
    const c = layers.find((l) => l.kind === 'chat') as ChatRouteLayer | undefined;
    return c ? [c.id] : [];
  },
};

// --- календарь ---------------------------------------------------------------

/** Встреча по ссылке; `at` — вхождение повторяющейся, в адрес пока не входит. */
export type CalendarLayer = { key: string; kind: 'event'; id: string; at?: string };
export const eventLayer = (id: string, at?: string): CalendarLayer =>
  at === undefined ? { key: `event:${id}`, kind: 'event', id } : { key: `event:${id}`, kind: 'event', id, at };

const calendar: Route<CalendarLayer> = {
  parse(seg) {
    return seg[0] === 'event' && seg[1] && ID.test(seg[1]) ? [eventLayer(seg[1])] : [];
  },
  path(layers) {
    const e = layers.find((l) => l.kind === 'event');
    return e ? ['event', e.id] : [];
  },
};

// --- подрядчики --------------------------------------------------------------

/**
 * «Сотрудники» и «Финансы» — только у нашего юрлица (раздел «Наши юрлица»); «Платежи» — заявки,
 * где компания получатель (спека 2026-10-07).
 */
export type ContractorTab = 'main' | 'checks' | 'contacts' | 'skills' | 'docs' | 'contracts' | 'payments' | 'history' | 'staff' | 'finance';
export const CONTRACTOR_TABS: ContractorTab[] = ['main', 'checks', 'contacts', 'skills', 'docs', 'contracts', 'payments', 'history', 'staff', 'finance'];

/*
 * Вкладки карточки договора — как у карточки подрядчика, поле слоя в адресе.
 *
 * «Согласование» — одна вкладка вместо прежних «Документ» и «Ход» (решение
 * Ильи 21.09.2026): схема, кнопки круга и сам текст с замечаниями — это один
 * разговор, и разносить его по двум вкладкам значило бы заставлять ведущего
 * круга смотреть на схему в одном месте, а нажимать кнопки в другом.
 */
export type ContractTab = 'terms' | 'approval' | 'payments' | 'amendments' | 'history';
export const CONTRACT_TABS: ContractTab[] = ['terms', 'approval', 'payments', 'amendments', 'history'];

export type ContractorsLayer =
  | { key: string; kind: 'card'; id: string; tab: ContractorTab }
  | { key: string; kind: 'check'; contractorId: string; id: string }
  /*
   * Вкладка договора необязательна, и пустая она значит не «данные», а «не
   * названа»: тогда вкладку выбирает сам договор по своему статусу
   * (`landingTab` в contracts/registry.ts). Пока умолчанием были «данные»,
   * сказать «открой, как сочтёшь нужным» было нечем — адрес без хвоста и
   * адрес с «данными» выглядели одинаково.
   */
  | { key: string; kind: 'contract'; contractorId: string; id: string; tab?: ContractTab }
  | { key: string; kind: 'compare'; contractorId: string; id: string }
  | {
      key: string;
      kind: 'newContract';
      contractorId: string;
      parentId?: string;
      /** Род допсоглашения: его задаёт кнопка раздела «Допсоглашения». */
      amendKind?: 'WORK' | 'CHANGE' | '';
      /** ДС об изменении: на чьей карточке нажали — его цепочку и меняют. */
      amendsId?: string;
    };

export const cardLayer = (id: string, tab: ContractorTab = 'main'): ContractorsLayer =>
  ({ key: `card:${id}`, kind: 'card', id, tab });
export const checkLayer = (contractorId: string, id: string): ContractorsLayer =>
  ({ key: `check:${id}`, kind: 'check', contractorId, id });
export const contractLayer = (contractorId: string, id: string, tab?: ContractTab): ContractorsLayer =>
  ({ key: `contract:${id}`, kind: 'contract', contractorId, id, tab });
/** Сравнение версий поверх договора: свой адрес, чтобы открывалось ссылкой. */
export const compareLayer = (contractorId: string, id: string): ContractorsLayer =>
  ({ key: `compare:${id}`, kind: 'compare', contractorId, id });
/**
 * Форма нового договора — слой без адреса, как мастер нового проекта.
 * Допсоглашение заводится ею же: `parentId` — договор семьи, `amendKind` —
 * род с кнопки раздела, `amendsId` — чью цепочку меняет ДС об изменении.
 * Всё попадает в ключ слоя: работа и изменение, изменение первой работы и
 * второй — разные формы, и общий ключ показал бы вторую с содержимым первой.
 */
export const newContractLayer = (
  contractorId: string,
  parentId = '',
  amendKind: 'WORK' | 'CHANGE' | '' = '',
  amendsId = '',
): ContractorsLayer => ({
  key: ['new-contract', contractorId, parentId, amendKind, amendsId].filter(Boolean).join(':'),
  kind: 'newContract',
  contractorId,
  parentId,
  amendKind,
  amendsId,
});

const contractorRoute: Route<ContractorsLayer> = {
  parse(seg) {
    if (!seg[0] || !ID.test(seg[0])) return [];
    if (seg[1] === 'checks' && seg[2] && ID.test(seg[2])) return [cardLayer(seg[0], 'checks'), checkLayer(seg[0], seg[2])];
    if (seg[1] === 'contracts' && seg[2] && ID.test(seg[2])) {
      // Сравнение открывается из панели документа, и под ним лежит эта же
      // вкладка: места для второго имени в адресе нет.
      if (seg[3] === 'compare') {
        return [
          cardLayer(seg[0], 'contracts'),
          contractLayer(seg[0], seg[2], 'approval'),
          compareLayer(seg[0], seg[2]),
        ];
      }
      // Хвоста нет — вкладку не назвали, и выберет её сам договор по статусу.
      const tab = CONTRACT_TABS.find((t) => t === seg[3]);
      return [cardLayer(seg[0], 'contracts'), contractLayer(seg[0], seg[2], tab)];
    }
    const tab = CONTRACTOR_TABS.find((t) => t === seg[1]) ?? 'main';
    return [cardLayer(seg[0], tab)];
  },
  path(layers) {
    const card = layers.find((l) => l.kind === 'card');
    if (card?.kind !== 'card') return [];
    const check = layers.find((l) => l.kind === 'check');
    if (check?.kind === 'check') return [card.id, 'checks', check.id];
    const contract = layers.find((l) => l.kind === 'contract');
    if (contract?.kind === 'contract') {
      const compare = layers.find((l) => l.kind === 'compare');
      if (compare) return [card.id, 'contracts', contract.id, 'compare'];
      const base = [card.id, 'contracts', contract.id];
      // Неназванная вкладка хвоста не пишет: адрес говорит ровно то, что
      // человек выбрал, а невыбранное договор решит сам при следующем чтении.
      return contract.tab ? [...base, contract.tab] : base;
    }
    return card.tab === 'main' ? [card.id] : [card.id, card.tab];
  },
};

// --- договоры ----------------------------------------------------------------

export type ContractsSub = 'registry' | 'counterparties';
export const CONTRACTS_SUBS: ContractsSub[] = ['registry', 'counterparties'];

/**
 * Вкладки карточки фонда (слой `org`): у чужого фонда «Проектов» нет, а «Сотрудников» и «Финансов» —
 * только у нашего, но поле слоя не проверяет доступность — решает экран.
 */
export type OrgTab = 'main' | 'docs' | 'contacts' | 'contracts' | 'projects' | 'staff' | 'finance';
export const ORG_TABS: OrgTab[] = ['main', 'docs', 'contacts', 'contracts', 'projects', 'staff', 'finance'];

export type ContractsLayer =
  | ContractorsLayer
  | { key: string; kind: 'sub'; sub: ContractsSub }
  | { key: string; kind: 'org'; id: string; tab: OrgTab }
  /*
   * Договор реестра (часть 2): открыт из «Реестра договоров» или из карточки
   * контрагента, без карточки подрядчика под ним. Вкладка — как у договора
   * подрядчика: пустая значит «не названа».
   */
  | { key: string; kind: 'deal'; id: string; tab?: ContractTab }
  | { key: string; kind: 'dealCompare'; id: string }
  /**
   * Форма договора реестра: `orgId` — сторона подставлена, пусто — выбирается в форме.
   * `preset` — заготовка типового договора функции (спека 2026-09-30).
   * `workId` — работа подставлена и заперта, остальное свободно: «Заключить
   * другой договор» на здании (спека 2026-10-01).
   */
  | {
      key: string; kind: 'newDeal'; orgId: string; parentId: string; amendKind: 'WORK' | 'CHANGE' | ''; amendsId: string;
      preset?: DealPreset; workId?: string;
    };

/**
 * Подраздел раздела «Договоры». Ключ всегда один и тот же — смена подраздела
 * идёт через `nav.push`, вершина стопки заменяется без новой записи в
 * истории: подраздел это состояние корня, а не отдельный экран.
 */
export const subLayer = (sub: ContractsSub): ContractsLayer => ({ key: 'sub', kind: 'sub', sub });
export const orgLayer = (id: string, tab: OrgTab = 'main'): ContractsLayer => ({ key: `org:${id}`, kind: 'org', id, tab });
export const dealLayer = (id: string, tab?: ContractTab): ContractsLayer => ({ key: `deal:${id}`, kind: 'deal', id, tab });
export const dealCompareLayer = (id: string): ContractsLayer => ({ key: `deal-compare:${id}`, kind: 'dealCompare', id });
/** Форма договора реестра — слой без адреса. Ключ различает стороны и ДС, как у `newContractLayer`. */
export const newDealLayer = (
  orgId = '',
  parentId = '',
  amendKind: 'WORK' | 'CHANGE' | '' = '',
  amendsId = '',
  preset?: DealPreset,
  workId?: string,
): ContractsLayer => ({
  key: ['new-deal', orgId, parentId, amendKind, amendsId, preset && `preset:${preset.projectId}:${preset.type}`, workId && `work:${workId}`]
    .filter(Boolean)
    .join(':'),
  kind: 'newDeal',
  orgId,
  parentId,
  amendKind,
  amendsId,
  preset,
  workId,
});

/**
 * Стопка, которой открывается договор извне раздела (уведомление, задание,
 * соседний договор): договор подрядчика — поверх его карточки, как прежде;
 * договор без подрядчика — в «Реестре договоров».
 */
export function contractStack(contractorId: string | null, contractId: string, tab?: ContractTab): ContractsLayer[] {
  return contractorId
    ? [subLayer('counterparties'), cardLayer(contractorId, 'contracts'), contractLayer(contractorId, contractId, tab)]
    : [subLayer('registry'), dealLayer(contractId, tab)];
}

const contracts: Route<ContractsLayer> = {
  parse(seg) {
    if (seg[0] === 'contractors') return [subLayer('counterparties'), ...contractorRoute.parse(seg.slice(1))];
    const sub = CONTRACTS_SUBS.find((s) => s === seg[0]);
    if (!sub) return [];
    if (sub === 'counterparties' && seg[1] && ID.test(seg[1])) {
      const tab = ORG_TABS.find((t) => t === seg[2]) ?? 'main';
      return [subLayer(sub), orgLayer(seg[1], tab)];
    }
    if (sub === 'registry' && seg[1] && ID.test(seg[1])) {
      // Сравнение — поверх вкладки согласования, как у договора подрядчика.
      if (seg[2] === 'compare') return [subLayer(sub), dealLayer(seg[1], 'approval'), dealCompareLayer(seg[1])];
      return [subLayer(sub), dealLayer(seg[1], CONTRACT_TABS.find((t) => t === seg[2]))];
    }
    return [subLayer(sub)];
  },
  path(layers) {
    if (layers.some((l) => l.kind === 'card')) return ['contractors', ...contractorRoute.path(layers as ContractorsLayer[])];
    /*
     * Договор адресуется реестром, откуда бы его ни открыли: из карточки
     * контрагента он тоже пишет /contracts/registry/<id>. Второго адреса у
     * договора нет, и ссылка им открывается у любого, кому открыт реестр.
     * В адресе верхний договор; цепочка соседей живёт в истории браузера.
     */
    const deal = [...layers].reverse().find((l) => l.kind === 'deal' || l.kind === 'dealCompare');
    if (deal?.kind === 'dealCompare') return ['registry', deal.id, 'compare'];
    if (deal?.kind === 'deal') return deal.tab ? ['registry', deal.id, deal.tab] : ['registry', deal.id];
    const org = layers.find((l) => l.kind === 'org');
    if (org?.kind === 'org') return org.tab === 'main' ? ['counterparties', org.id] : ['counterparties', org.id, org.tab];
    const sub = layers.find((l) => l.kind === 'sub');
    return sub?.kind === 'sub' ? [sub.sub] : [];
  },
};

// --- наши юрлица -------------------------------------------------------------

/*
 * «Наши юрлица» — раздел первого уровня (спека 2026-10-01). Страница юрлица —
 * его карточка компании (`card`) или фонда (`org`): те же экраны, что в
 * «Договорах», но поверх своего корня с плитками.
 */
export type LegalLayer =
  | { key: string; kind: 'card'; id: string; tab: ContractorTab }
  | { key: string; kind: 'org'; id: string; tab: OrgTab };

/** Смена вкладки — тот же ключ: вершина стопки заменяется без новой записи в истории. */
export const legalCardLayer = (id: string, tab: ContractorTab = 'main'): LegalLayer =>
  ({ key: `card:${id}`, kind: 'card', id, tab });
export const legalFundLayer = (id: string, tab: OrgTab = 'main'): LegalLayer =>
  ({ key: `org:${id}`, kind: 'org', id, tab });

const legal: Route<LegalLayer> = {
  parse(seg) {
    if (seg[0] === 'card' && seg[1] && ID.test(seg[1])) {
      return [legalCardLayer(seg[1], CONTRACTOR_TABS.find((t) => t === seg[2]) ?? 'main')];
    }
    if (seg[0] === 'fund' && seg[1] && ID.test(seg[1])) {
      return [legalFundLayer(seg[1], ORG_TABS.find((t) => t === seg[2]) ?? 'main')];
    }
    return [];
  },
  path(layers) {
    const top = layers[layers.length - 1];
    if (!top) return [];
    const head = top.kind === 'card' ? 'card' : 'fund';
    return top.tab === 'main' ? [head, top.id] : [head, top.id, top.tab];
  },
};

// --- финансы -----------------------------------------------------------------

/*
 * Раздел «Финансы» (спека 2026-10-07): корень — вкладки текущего реестра,
 * прошлых и всех заявок; реестр открывается экраном поверх со своим адресом
 * /finance/registers/<id> — по нему ведёт задание из «Моих заданий».
 */
export type FinanceLayer = { key: string; kind: 'register'; id: string };
export const registerLayer = (id: string): FinanceLayer => ({ key: `register:${id}`, kind: 'register', id });

const finance: Route<FinanceLayer> = {
  parse(seg) {
    return seg[0] === 'registers' && seg[1] && ID.test(seg[1]) ? [registerLayer(seg[1])] : [];
  },
  path(layers) {
    const r = layers.find((l) => l.kind === 'register');
    return r ? ['registers', r.id] : [];
  },
};

// --- реестр ------------------------------------------------------------------

/** Раздел без вложенных адресов: корень и только. */
const none: Route = { parse: () => [], path: () => [] };

export const ROUTES: Record<Section, Route> = {
  home: none,
  kb,
  projects,
  chat,
  calendar,
  contracts,
  legal,
  finance,
  tasks: none,
  admin: none,
  profile: none,
  more: none,
};

/** Старые адреса страниц базы знаний, когда она жила в корне (до 17.09.2026). */
const LEGACY_KB = ['org', 'groups', 'gloss', 'map'];

/**
 * Первый сегмент → раздел, остаток → его маршруту. `null` — адрес не про
 * место в приложении (комната гостя, договор по гостевой ссылке).
 */
export function splitPath(pathname: string): { section: Section; rest: string[] } | null {
  const seg = pathname.split('/').filter(Boolean);
  if (seg[0] === 'room' || seg[0] === 'g') return null;
  if (seg.length === 0) return { section: 'home', rest: [] };
  if (LEGACY_KB.includes(seg[0])) return { section: 'kb', rest: seg };
  // Старые адреса «Подрядчиков» (до 26.09.2026): ими делились ссылками и они
  // лежат в закладках. Хвост тот же, раздел — «Договоры».
  if (seg[0] === 'contractors') return { section: 'contracts', rest: seg };
  // «Наши компании» жили подразделом «Договоров» (до 01.10.2026), теперь это раздел «Наши юрлица».
  if (seg[0] === 'contracts' && seg[1] === 'legal') return { section: 'legal', rest: [] };
  const section = (Object.keys(ROOT) as Section[]).find((s) => ROOT[s] === seg[0]);
  // неизвестный корень — Главная
  return section ? { section, rest: seg.slice(1) } : { section: 'home', rest: [] };
}

export function joinPath(section: Section, segments: string[]): string {
  return '/' + [ROOT[section], ...segments].filter(Boolean).join('/');
}

/**
 * Место по адресу при загрузке. `null` — адрес чужой (комната гостя, ссылка
 * приглашения, стенд предпросмотра): контроллер тогда ведёт стопку, но адрес
 * не трогает.
 */
export function placeFrom(pathname: string | null, search = ''): Place | null {
  if (pathname === null) return null;
  const q = new URLSearchParams(search);
  if (q.has('invite')) return null;
  // Старые ссылки из уведомлений (до 17.09.2026): ?chat=<id> и ?event=<id>.
  // Живут один релиз после выкладки этой работы — пуши, отправленные раньше,
  // ещё лежат в шторках телефонов. Потом снять.
  const chatId = q.get('chat');
  if (chatId && ID.test(chatId)) return { section: 'chat', layers: ROUTES.chat.parse([chatId]) };
  const eventId = q.get('event');
  if (eventId && ID.test(eventId)) return { section: 'calendar', layers: ROUTES.calendar.parse(['event', eventId]) };
  const split = splitPath(pathname);
  if (!split) return null;
  return { section: split.section, layers: ROUTES[split.section].parse(split.rest) };
}
